import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  CustomerLoyalty,
  LoyaltyConfig,
  UpsertLoyaltyConfigBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

const CONFIG_PATH = '/api/restaurants/{restaurantId}/loyalty-config';

const configKey = (restaurantId: string) => ['loyalty-config', restaurantId] as const;
const customerKey = (restaurantId: string, customerId: string) =>
  ['loyalty-customer', restaurantId, customerId] as const;

export function useLoyaltyConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: configKey(restaurantId),
    queryFn: async () =>
      unwrapResult<LoyaltyConfig>(
        await openapiClient.GET(CONFIG_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useUpsertLoyaltyConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpsertLoyaltyConfigBody) =>
      unwrapResult<LoyaltyConfig>(
        await openapiClient.PUT(CONFIG_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKey(restaurantId) });
      trackEvent('setup_changed', { area: 'loyalty' });
    },
  });
}

export function useCustomerLoyalty(customerId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: customerKey(restaurantId, customerId),
    queryFn: async () =>
      unwrapResult<CustomerLoyalty>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/loyalty/customers/{customerId}', {
          params: { path: { restaurantId, customerId } },
        }),
      ),
    enabled: !!restaurantId && !!customerId,
  });
}

export function useAdjustPoints(customerId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ points, reason }: { points: number; reason: string }) =>
      unwrapResult<CustomerLoyalty>(
        await openapiClient.POST(
          '/api/restaurants/{restaurantId}/loyalty/customers/{customerId}/adjust',
          { params: { path: { restaurantId, customerId } }, body: { points, reason } },
        ),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerKey(restaurantId, customerId) }),
  });
}
